<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useDark } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

import AppButton from '@/components/atoms/buttons/AppButton.vue'
import MobileUserMenu from '@/components/atoms/sidebars/components/MobileUserMenu.vue'
import SidebarDrawer from '@/components/atoms/sidebars/components/SidebarDrawer.vue'
import AppAICreditsFloatingButton from '@/components/molecules/ai/AppAICreditsFloatingButton.vue'
import IconInfo from '@/assets/svg/IconInfo.svg'
import IconPhone from '@/assets/svg/icon-phone.svg'
import { useSidebarRoutes } from '@/components/atoms/sidebars/composables/useSidebarRoutes'
import { useWebphonePermissions } from '@/composables/useWebphonePermissions'
import { useAuthStore } from '@/stores/useAuthStore'

const visible = ref(false)
const { routes } = useSidebarRoutes()
const mobileRoutes = computed(() => routes.value.filter(route => route.path !== '/notes'))
const authStore = useAuthStore()
const isDark = useDark({ selector: 'html' })
const { t } = useI18n()
const extensionNumber = authStore.extension?.number || 'N/A'
const { shouldBlockWebphone, checkPermissions, tooltipKey, microphonePermission, soundPermission } =
  useWebphonePermissions()
const isWebphoneDisabled = computed(
  () => !authStore.webphoneAvailable || shouldBlockWebphone.value,
)
const webphoneTooltip = computed(() =>
  shouldBlockWebphone.value
    ? t(tooltipKey.value)
    : `${t('common.destination_type.extension')}: ${extensionNumber}`,
)

const refreshWebphonePermissions = () => {
  void checkPermissions()
}

const handleWebphoneClick = () => {
  if (shouldBlockWebphone.value) return

  authStore.toggleWebphone()
}

onMounted(() => {
  refreshWebphonePermissions()
  window.addEventListener('focus', refreshWebphonePermissions)
  document.addEventListener('visibilitychange', refreshWebphonePermissions)
})

onBeforeUnmount(() => {
  window.removeEventListener('focus', refreshWebphonePermissions)
  document.removeEventListener('visibilitychange', refreshWebphonePermissions)
})
</script>

<template>
  <div class="w-full flex flex-col">
    <header
      class="app-glass backdrop-blur-xl backdrop-saturate-150 w-full flex items-center gap-2 px-3 py-2 border-b border-slate-200/70 dark:border-white/10 !shadow-xs"
      :class="isDark ? 'app-glass--dark text-slate-100' : 'text-slate-800'"
    >
      <div class="flex items-center justify-start" style="width: 44px">
        <AppButton
          icon="pi pi-bars"
          text
          rounded
          @click="visible = true"
          :class="isDark ? '!text-(--p-primary-color)' : '!text-(--color-primary-500)'"
        />
      </div>

      <div class="flex-1 flex items-center justify-center gap-2">
        <!-- <AppAICreditsFloatingButton compact allow-purchase /> -->

        <button
          @click="handleWebphoneClick"
          class="relative flex h-10 w-26 items-center justify-center rounded-lg transition-all duration-200 active:scale-95"
          :class="
            isWebphoneDisabled
              ? 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-(--p-surface-800) dark:text-gray-400'
              : isDark
                ? 'cursor-pointer hover:bg-(--p-surface-800)'
                : 'hover:bg-(--p-primary-color)/10'
          "
        >
          <IconPhone
            width="44"
            :class="isWebphoneDisabled ? 'text-gray-400' : 'text-(--p-primary-color)'"
            :stroke-width="1.8"
          />
          <span
            class="absolute inset-0 flex items-center justify-center pl-3 text-[11px] font-medium leading-none"
            :class="isWebphoneDisabled ? 'text-gray-400' : 'text-(--p-primary-color)'"
          >
            {{ extensionNumber }}
          </span>
        </button>

        <button
          v-if="shouldBlockWebphone"
          type="button"
          class="flex h-9 w-9 items-center justify-center rounded-full text-amber-400 transition hover:bg-amber-50 dark:hover:bg-amber-400/10"
          v-tooltip.bottom="webphoneTooltip"
          @mouseenter="refreshWebphonePermissions"
          @focus="refreshWebphonePermissions"
        >
          <IconInfo class="h-5 w-5" />
        </button>

        <span
          v-if="shouldBlockWebphone"
          class="text-[10px] leading-tight text-amber-600 dark:text-amber-400"
        >
          mic: {{ microphonePermission }} | snd: {{ soundPermission }}
        </span>
      </div>

      <div class="flex items-center justify-end" style="width: 44px">
        <MobileUserMenu />
      </div>
    </header>

    <SidebarDrawer v-model:visible="visible" :routes="mobileRoutes" />
  </div>
</template>
